import { Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  of,
  pairwise,
  switchMap
} from 'rxjs';

import {
  DeployChannelStats,
  DeployCountKind,
  DeployPlanConfigItem,
  DeployPlanSummaryItem,
  DeployPlanSummaryResponse
} from '@core/models/deploy-plan-config.model';
import { KNOWN_FEATURE_FLAGS } from '@core/models/feature-flags/known-feature-flags';
import { PlanDeployStatus } from '@core/models/plan.model';
import { PlanningSpaceMetadataModel } from '@core/models/planning-space/planning-space.model';
import { DeployPlanConfigService } from '@core/services/deploy-plan-config/deploy-plan-config.service';
import { FeatureFlagsFacade } from '@core/services/feature-flags/feature-flags.facade';
import { ICONS } from '@core/services/icons/icons.service';
import { PlanningSpaceService } from '@core/services/planning-space/planning-space.service';
import { WebsocketService } from '@core/services/websocket/websocket.service';
import { selectDataspaceModel } from '@core/store/dataspace/dataspace.selectors';
import * as PlanActions from '@core/store/plan/plan.actions';
import * as PlanSelectors from '@core/store/plan/plan.selectors';
import { isNil } from '@core/utils/shared-utils';

import { PlanEditBarComponent } from '@features/components/plan/edit/plan-edit-bar/plan-edit-bar.component';

import { DeployChannelListComponent } from './deploy-channel-list/deploy-channel-list.component';
import { DeployChannelPanelComponent } from './deploy-channel-panel/deploy-channel-panel.component';
import { deriveChannelStats } from './deploy-channel-stats.utils';
import { DeployMessagesStore } from './deploy-messages.store';

/** `0` for a configuration that has never reported, which every real status postdates. */
const statusTime = (status: PlanDeployStatus | undefined): number => {
  if (status?.updatedAt === undefined) return 0;
  const time = new Date(status.updatedAt).getTime();
  return Number.isNaN(time) ? 0 : time;
};

@Component({
  selector: 'app-plan-deploy',
  imports: [
    TranslatePipe,
    DeployChannelListComponent,
    DeployChannelPanelComponent,
    PlanEditBarComponent
  ],
  providers: [DeployMessagesStore],
  templateUrl: './plan-deploy.component.html',
  styles: ``
})
export class PlanDeployComponent {
  protected readonly ICONS = ICONS;

  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly messagesStore = inject(DeployMessagesStore);
  private readonly deployPlanConfigService = inject(DeployPlanConfigService);
  private readonly featureFlags = inject(FeatureFlagsFacade);
  private readonly websocket = inject(WebsocketService);
  private readonly planningSpaceService = inject(PlanningSpaceService);

  /** Last status seen per configuration, so only a transition triggers a refetch. */
  private readonly lastSeenStatus = new Map<string, PlanDeployStatus['status']>();

  /**
   * For a deploy this page started: the time of the status it superseded, keyed by
   * configuration. A terminal status newer than this *is* that deploy reporting back,
   * whether or not its PROCESSING was ever observed here.
   *
   * Both sides are server-stamped, so the comparison stays on one clock — the optimistic
   * PROCESSING dispatched beside it carries local time and is deliberately not the baseline.
   */
  private readonly deployMarkBaseline = new Map<string, number>();

  protected readonly planMetadata = this.store.selectSignal(PlanSelectors.selectPlan);

  // Feature-gated: unknown/missing flags evaluate to false, so deploying stays disabled
  // until the flag is activated for the dataspace.
  protected readonly deployEnabled = this.featureFlags.isActive(
    KNOWN_FEATURE_FLAGS.ENABLE_DEPLOY_PLAN
  );

  /**
   * The plan's planning space, for its `isInternal` marker alone — the plan carries only
   * the space's id and name.
   */
  private readonly planningSpace = toSignal<PlanningSpaceMetadataModel | undefined>(
    toObservable(this.planMetadata).pipe(
      map((metadata) => metadata?.planningSpace?.id ?? null),
      distinctUntilChanged(),
      switchMap((planningSpaceId) => {
        if (isNil(planningSpaceId)) {
          return of(undefined);
        }
        return this.planningSpaceService.fetchOne$({ planningSpaceId }).pipe(
          catchError((error) => {
            console.error('Error fetching planning space:', error);
            return of(undefined);
          })
        );
      })
    ),
    { initialValue: undefined }
  );

  /**
   * GBE refuses to deploy a plan in an internal space for every role, ROOT included, and
   * answers 404 on the plan rather than 403. Staff can see the space, so an enabled action
   * here would hand them a bare "not found"; the remedy is to make the space visible again.
   *
   * Only a positive `isInternal` blocks: a failed read must not lock deployment behind a
   * reason that may not hold. Reading what a channel would send stays allowed either way,
   * which is why this gates the panel's Deploy rather than the `/messages` load.
   */
  protected readonly deployBlockedByInternal = computed(
    () => this.planningSpace()?.isInternal === true
  );

  /** Deploying needs both the feature flag and a space GBE will accept a deploy for. */
  protected readonly deployAllowed = computed(
    () => this.deployEnabled() && !this.deployBlockedByInternal()
  );

  protected readonly configs = signal<DeployPlanConfigItem[]>([]);
  private readonly summaryByConfigId = signal<ReadonlyMap<string, DeployPlanSummaryItem>>(
    new Map()
  );
  protected readonly isLoadingList = signal(true);
  protected readonly loadError = signal(false);
  protected readonly selectedConfigId = signal<string | null>(null);

  protected readonly selectedConfig = computed<DeployPlanConfigItem | null>(
    () => this.configs().find((item) => item.id === this.selectedConfigId()) ?? null
  );

  protected readonly planId = computed<string | null>(
    () => this.planMetadata()?.id ?? null
  );

  protected readonly deployStatusByConfigId = computed<
    ReadonlyMap<string, PlanDeployStatus>
  >(() => {
    const map = new Map<string, PlanDeployStatus>();
    for (const status of this.planMetadata()?.deployStatus ?? []) {
      map.set(status.configId, status);
    }
    return map;
  });

  /**
   * Two sources, deliberately. The websocket carries the counts with the terminal event, which
   * is what makes the channel repaint the instant a deploy the operator watched finishes; but
   * it has no replay, so a cold page load or a reconnect after a missed event has nothing to
   * read, and only `/deploy-summary` can answer for those.
   */
  protected readonly deployStatsByConfigId = computed<
    ReadonlyMap<string, DeployChannelStats>
  >(() => {
    const merged = new Map<string, DeployChannelStats>(this.summaryByConfigId());

    // A channel the operator has opened carries its own answer, and a better one: the summary
    // reports what the *deploys* recorded, which is nothing at all for a configuration last
    // deployed before GBE persisted counts, while the loaded messages carry the send history
    // itself. Costs nothing — the response is already cached.
    for (const config of this.configs()) {
      const derived = deriveChannelStats(this.messagesStore.state(config.id).response);
      if (derived !== null) merged.set(config.id, derived);
    }

    for (const [configId, status] of this.deployStatusByConfigId()) {
      // A status with no counts is either still PROCESSING or predates them; either way it
      // says nothing about how much went out, so the summary's answer stands.
      if (status.sentCount === undefined) continue;
      merged.set(configId, {
        // The event carries no count kind and a channel's kind does not change between
        // deploys, so whatever the summary or the loaded messages established still holds.
        countKind: merged.get(configId)?.countKind ?? DeployCountKind.SINGLE_PAYLOAD,
        totalMessages: status.totalMessages ?? null,
        sentCount: status.sentCount,
        failedCount: status.failedCount ?? 0
      });
    }
    return merged;
  });

  constructor() {
    // On a hard refresh the dataspace is not yet resolved when the component initializes,
    // so a one-shot read would permanently show the error state.
    this.store
      .select(selectDataspaceModel)
      .pipe(
        filter((ds) => !isNil(ds)),
        map((ds) => ds!.id),
        distinctUntilChanged(),
        switchMap((dataspaceId) => {
          this.isLoadingList.set(true);
          this.loadError.set(false);
          return this.deployPlanConfigService.list$(dataspaceId).pipe(
            map((res) => ({ items: res.items, error: false })),
            catchError(() => of({ items: [] as DeployPlanConfigItem[], error: true }))
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe(({ items, error }) => {
        this.configs.set(items);
        this.loadError.set(error);
        this.isLoadingList.set(false);
      });

    // What the channel list reports on a cold load. The plan's own response deliberately does
    // not carry counts — it is fetched on every plan navigation, including from pages that do
    // not care — so they come from here instead.
    toObservable(this.planId)
      .pipe(
        filter((id) => !isNil(id)),
        distinctUntilChanged(),
        switchMap((id) => this.deploySummary$(id!)),
        takeUntilDestroyed()
      )
      .subscribe((items) => {
        if (!isNil(items)) this.setSummary(items);
      });

    // `deploy-plan:status` is pure push with no replay, and the socket drops on navigation and
    // on a dev-server reload. A deploy whose PROCESSING *and* terminal event both land in that
    // gap is never reported at all, so the settle effect below never runs again and the row
    // stays badged "Sending" with no exit short of a page reload. Re-reading the authoritative
    // state on reconnect is what gives it one: the refetched plan replaces the optimistic
    // PROCESSING, which the effect then settles on with no special case of its own.
    this.websocket.connected$
      .pipe(
        distinctUntilChanged(),
        // A transition, not the current value. `connected$` is a BehaviorSubject, so arriving
        // with the socket already up replays a lone `true` and this correctly stays silent.
        // Arriving while it is *down* does fire, one cold-load read deliberately paid: that is
        // exactly the case where a status may already have been missed.
        pairwise(),
        filter(([wasConnected, isConnected]) => !wasConnected && isConnected),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        const planId = this.planId();
        if (isNil(planId)) return;
        // Dispatched rather than called on the service, so every other consumer of the plan
        // store sees the refreshed statuses too.
        this.store.dispatch(PlanActions.fetchPlans({ id: planId }));
        // Read here rather than left to the settle effect, which only reads it when something
        // settled: a reconnect with nothing in flight would otherwise keep counts a deploy in
        // another tab has already moved. Bounded at two reads per reconnect, and it cannot
        // loop — the trigger is the socket, never a signal this handler writes.
        this.deploySummary$(planId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((response) => {
            if (!isNil(response)) this.setSummary(response);
          });
      });

    // A deploy runs on a queue and reports over the websocket, so the `/messages` response
    // cached before it is stale the moment it finishes: `sends`, the Sent badge, the row
    // locks and "Last send" all still describe the state before the send. Refetching on the
    // terminal status is what makes the table settle without a page reload.
    effect(() => {
      const planId = this.planId();
      const statuses = this.deployStatusByConfigId();
      if (isNil(planId)) return;

      let settled = false;
      for (const [configId, status] of statuses) {
        const previous = this.lastSeenStatus.get(configId);
        this.lastSeenStatus.set(configId, status.status);
        if (status.status === 'PROCESSING') continue;

        // Two ways a deploy is known to have finished, and both are needed. Witnessing
        // PROCESSING covers one started elsewhere — another tab, another operator. The
        // baseline covers the one this page started, whose PROCESSING it may never see:
        // a deploy that finishes in a second can report back before the 202 it answers
        // has been handled, and nothing arrives afterwards to correct it.
        const baseline = this.deployMarkBaseline.get(configId);
        const reportsOurDeploy = baseline !== undefined && statusTime(status) > baseline;
        if (previous !== 'PROCESSING' && !reportsOurDeploy) continue;

        this.deployMarkBaseline.delete(configId);
        settled = true;
        this.messagesStore.clearDeploying(configId);
        const config = this.configs().find((item) => item.id === configId);
        if (config !== undefined) {
          this.messagesStore.refreshSendsIfLoaded(config, planId);
        }
      }

      // Once for the whole batch, not once per channel: the summary answers for all of them,
      // and two channels settling together would otherwise fire the same read twice. The
      // terminal event has already repainted the counts — this is the authoritative read
      // behind it, and the only source of `lastSentAt` for a channel with no message history.
      if (settled) {
        this.deploySummary$(planId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((response) => {
            if (!isNil(response)) this.setSummary(response);
          });
      }
    });
  }

  /**
   * Never raises: the counts are an enrichment of a page that works without them, so a failed
   * read leaves the previous answer in place and the rows keep their plain status badge.
   */
  private deploySummary$(planId: string) {
    return this.deployPlanConfigService
      .deploySummary$(planId)
      .pipe(catchError(() => of(null)));
  }

  private setSummary(response: DeployPlanSummaryResponse): void {
    this.summaryByConfigId.set(
      new Map(response.items.map((item) => [item.configId, item]))
    );
  }

  protected selectConfig(item: DeployPlanConfigItem): void {
    this.selectedConfigId.set(item.id);
  }

  /**
   * When a configuration last deployed successfully.
   *
   * `/deploy-summary` answers this exactly and for every channel type, including the
   * `view`-kind ones whose `/messages` response carries no send history at all. The status row
   * is only the fallback for a summary that has not arrived: it dates the *deploy* rather than
   * the send, and a channel whose latest deploy errored reports nothing there even though an
   * earlier one did go out.
   *
   * `Date | string` because the sources disagree: the websocket reducer converts to a `Date`,
   * while the summary and a freshly fetched plan both carry the wire's ISO string.
   */
  protected lastSentAtFor(configId: string): Date | string | null {
    const summary = this.summaryByConfigId().get(configId);
    if (!isNil(summary)) return summary.lastSentAt;

    const status = this.deployStatusByConfigId().get(configId);
    if (isNil(status) || status.status !== 'SUCCESS') return null;
    return status.updatedAt ?? null;
  }

  /**
   * The websocket stream is the source of truth for deploy status, but its first message
   * can lag the `202`; the optimistic entry makes the channel's badge move immediately.
   */
  protected onDeployStarted(configId: string): void {
    const planId = this.planId();
    if (isNil(planId)) return;
    // Read before the dispatch below, so it holds the status this deploy supersedes rather
    // than the optimistic PROCESSING about to replace it.
    this.deployMarkBaseline.set(
      configId,
      statusTime(this.deployStatusByConfigId().get(configId))
    );
    this.store.dispatch(
      PlanActions.deployStatusUpdated({
        planId,
        configId,
        status: 'PROCESSING',
        updatedAt: Date.now()
      })
    );
  }
}
